import { useEffect, useState } from "react";
import { loadBrand } from "./registry.js";
import type { BrandModule } from "./types.js";

// The default brand's icons, shipped as static files in each app's `public/` and already wired
// into its `index.html` (so there's a correct favicon before this ever runs, and for crawlers/
// contexts that don't execute JS at all). A brand with no `favicon` of its own — everything
// today except Startle — resets to these rather than leaving a previous brand's icon lingering
// in the tab after a `?brand=` switch.
const DEFAULT_FAVICON_ICO_HREF = "/favicon.ico";
const DEFAULT_TOUCH_ICON_HREF = "/apple-touch-icon.png";

/**
 * Loads a brand module by key and applies its tokens as CSS custom properties on `<html>`, plus
 * its favicon (or the default app icon, if the brand doesn't ship its own) on `<head>`.
 * Combine with `useBrand()` from `@touchscreen/hooks` for the key itself — kept separate so
 * neither package depends on the other.
 */
export function useBrandModule(brandKey: string): { brand: BrandModule | null; loading: boolean } {
  const [brand, setBrand] = useState<BrandModule | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    loadBrand(brandKey).then((mod) => {
      if (cancelled) return;
      setBrand(mod);
      applyBrandTokens(mod.tokens);
      applyBrandFavicon(mod.favicon);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [brandKey]);

  return { brand, loading };
}

export function applyBrandTokens(tokens: BrandModule["tokens"]): void {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  for (const [name, value] of Object.entries(tokens)) {
    root.style.setProperty(name, value);
  }
}

/**
 * Points the tab/bookmark/home-screen icon `<link>`s at this brand's favicon, or resets them to
 * the app's default (Startle's) if the brand — like every non-Startle brand today — doesn't ship
 * one. Updates existing `<link>` tags in place rather than appending duplicates, since this can
 * re-run whenever `?brand=` changes within a session.
 */
export function applyBrandFavicon(favicon: BrandModule["favicon"]): void {
  if (typeof document === "undefined") return;
  setLinkHref("icon", favicon?.ico ?? DEFAULT_FAVICON_ICO_HREF);
  setLinkHref("apple-touch-icon", favicon?.touchIcon ?? DEFAULT_TOUCH_ICON_HREF);
}

function setLinkHref(rel: string, href: string): void {
  let link = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!link) {
    link = document.createElement("link");
    link.rel = rel;
    document.head.appendChild(link);
  }
  link.href = href;
}
